import { FileUp, Calculator, DraftingCompass, Factory, ClipboardCheck, Truck } from "lucide-react";
import { FadeUp, SectionHeading, CTASection } from "../components/Shared";

const STEPS = [
  { icon: FileUp, title: "Upload Your Design", desc: "Share CAD files (STEP, IGES, DXF, PDF drawings) securely through our portal along with quantities, materials and tolerances." },
  { icon: Calculator, title: "Receive Your Quote", desc: "Our AI-assisted quoting engine and engineering team return a transparent, itemized quote — typically within 24 hours." },
  { icon: DraftingCompass, title: "Engineering Review", desc: "Before production, every part undergoes DFM analysis. We flag risks, suggest optimizations and lock the specification." },
  { icon: Factory, title: "Smart Production Matching", desc: "Your order is routed to the best-fit vetted manufacturer in our network based on capability, capacity and quality record." },
  { icon: ClipboardCheck, title: "Quality Assurance", desc: "In-process checkpoints and final inspection with documented reports, material certs and dimensional data." },
  { icon: Truck, title: "Delivery & Support", desc: "Managed logistics to your dock, with full traceability and post-delivery engineering support for repeat orders." },
];

export default function HowItWorks() {
  return (
    <div data-testid="how-it-works-page">
      <section className="py-28 sm:py-36 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <FadeUp>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-primary mb-6 font-mono-tech">How It Works</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter max-w-3xl leading-[1.05]">
              Six steps from <span className="font-medium text-primary">file to finished part.</span>
            </h1>
            <p className="mt-8 text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed">
              A disciplined, transparent process designed to remove friction, risk and guesswork from custom manufacturing.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="py-24 sm:py-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-8">
          <div className="relative">
            <div className="absolute left-6 sm:left-7 top-0 bottom-0 w-px bg-gradient-to-b from-primary via-primary/50 to-accent" />
            <div className="space-y-14">
              {STEPS.map((s, i) => (
                <FadeUp key={s.title} delay={i * 0.05}>
                  <div className="relative flex gap-8 sm:gap-10" data-testid={`step-${i + 1}`}>
                    <div className="relative z-10 h-12 w-12 sm:h-14 sm:w-14 shrink-0 rounded-full bg-background border-2 border-primary flex items-center justify-center">
                      <s.icon className="h-5 w-5 sm:h-6 sm:w-6 text-primary" strokeWidth={1.5} />
                    </div>
                    <div className="pt-1 pb-2">
                      <span className="font-mono-tech text-[11px] font-bold tracking-[0.25em] text-accent">STEP 0{i + 1}</span>
                      <h3 className="mt-2 text-xl sm:text-2xl font-semibold font-heading">{s.title}</h3>
                      <p className="mt-3 text-muted-foreground leading-relaxed max-w-xl">{s.desc}</p>
                    </div>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTASection
        title="Start step one today."
        description="Upload your first design and experience a manufacturing process built around clarity and speed."
      />
    </div>
  );
}
